/**
 * EditRideModal — dispatcher-only ride editor. Every field is optional;
 * only changed fields are sent to POST /api/nemt/admin/rides/{id}/edit.
 * Full audit trail — each change appended to the ride's audit_log.
 */
import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, PencilLine, DollarSign } from "lucide-react";
import { adminPost } from "./utils";
import { AddressAutocomplete } from "../../components/AddressAutocomplete";

const TRIP_CLASSES = [
  { v: "local_oneway",             label: "Local one-way" },
  { v: "local_roundtrip",          label: "Local round trip" },
  { v: "long_distance_oneway",     label: "Long distance one-way" },
  { v: "long_distance_roundtrip",  label: "Long distance round trip" },
];

const dollars = (cents) => (cents == null ? "" : (cents / 100).toFixed(2));

export function EditRideModal({ auth, ride, onClose, onDone }) {
  const [form, setForm] = useState({
    member_name: ride.member_name || "",
    member_phone: ride.member_phone || "",
    member_email: ride.member_email || "",
    pickup: ride.pickup || "",
    dropoff: ride.dropoff || "",
    trip_date: ride.trip_date || "",
    trip_time: ride.trip_time || "",
    trip_class: ride.trip_class || "local_oneway",
    mobility: ride.mobility || "ambulatory",
    passengers: ride.passengers ?? "",
    wait_minutes: ride.wait_minutes ?? "",
    notes: ride.notes || "",
    suggested_final_fare_dollars: dollars(ride.suggested_final_fare_cents),
    hold_amount_dollars: dollars(ride.hold_amount_cents),
    final_fare_dollars: dollars(ride.final_fare_cents),
  });
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("Reason for edit is required"); return; }
    const body = { reason: reason.trim(), edited_by: "dispatcher" };
    // Only send changed fields — spare Mongo audit noise on no-op saves.
    const maybe = (k, val) => { if (val !== undefined && val !== "" && val != null) body[k] = val; };
    for (const k of ["member_name", "member_phone", "member_email", "pickup", "dropoff",
                      "trip_date", "trip_time", "trip_class", "mobility", "notes"]) {
      if ((form[k] ?? "") !== (ride[k] ?? "")) body[k] = form[k];
    }
    if (form.passengers !== (ride.passengers ?? "")) maybe("passengers", parseInt(form.passengers, 10));
    if (form.wait_minutes !== (ride.wait_minutes ?? "")) maybe("wait_minutes", parseInt(form.wait_minutes, 10));
    if (pickupCoords) { body.pickup_lat = pickupCoords.lat; body.pickup_lng = pickupCoords.lng; }
    if (dropoffCoords) { body.dropoff_lat = dropoffCoords.lat; body.dropoff_lng = dropoffCoords.lng; }
    // Pricing fields — convert dollars → cents
    const toCents = (dollarsStr) => {
      if (dollarsStr === "" || dollarsStr == null) return null;
      const n = parseFloat(dollarsStr);
      return isNaN(n) ? null : Math.round(n * 100);
    };
    const fareCents = toCents(form.suggested_final_fare_dollars);
    if (fareCents !== null && fareCents !== ride.suggested_final_fare_cents) body.suggested_final_fare_cents = fareCents;
    const holdCents = toCents(form.hold_amount_dollars);
    if (holdCents !== null && holdCents !== ride.hold_amount_cents) body.hold_amount_cents = holdCents;
    const finalCents = toCents(form.final_fare_dollars);
    if (finalCents !== null && finalCents !== ride.final_fare_cents) body.final_fare_cents = finalCents;

    setBusy(true);
    try {
      const r = await adminPost(auth, `rides/${ride.id}/edit`, body);
      if (r.status === "unchanged") toast("No changes to save");
      else toast.success(`Saved ${r.changed_fields.length} field${r.changed_fields.length === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (err) { toast.error(err.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto"
          data-testid="edit-ride-modal"
          onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 shadow-2xl my-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-brand-deep">
              <PencilLine className="w-5 h-5 text-brand-green" />
              <h2 className="font-serif text-xl">Edit ride</h2>
            </div>
            <div className="text-xs text-brand-muted mt-1">
              {ride.member_name} · {ride.trip_date} at {ride.trip_time} · currently <strong>{ride.status}</strong>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-brand-muted hover:text-red-600" data-testid="edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Customer */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input value={form.member_name} onChange={set("member_name")} placeholder="Full name" data-testid="edit-name"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <input value={form.member_phone} onChange={set("member_phone")} placeholder="Phone" data-testid="edit-phone"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <input value={form.member_email} onChange={set("member_email")} placeholder="Email" type="email" data-testid="edit-email"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
        </div>

        {/* Addresses (with autocomplete) */}
        <div className="space-y-2">
          <AddressAutocomplete value={form.pickup}
            onChange={(v, coords) => { setForm({ ...form, pickup: v }); setPickupCoords(coords || null); }}
            placeholder="Pickup address" testid="edit-pickup"
            className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <AddressAutocomplete value={form.dropoff}
            onChange={(v, coords) => { setForm({ ...form, dropoff: v }); setDropoffCoords(coords || null); }}
            placeholder="Dropoff address (facility, clinic, hospital…)" testid="edit-dropoff"
            className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
        </div>

        {/* Schedule + trip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <input type="date" value={form.trip_date} onChange={set("trip_date")} data-testid="edit-date"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <input type="time" value={form.trip_time} onChange={set("trip_time")} data-testid="edit-time"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <select value={form.trip_class} onChange={set("trip_class")} data-testid="edit-class"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm">
            {TRIP_CLASSES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
          <select value={form.mobility} onChange={set("mobility")} data-testid="edit-mobility"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm">
            <option value="ambulatory">Ambulatory</option>
            <option value="wheelchair">Wheelchair</option>
            <option value="stretcher">Stretcher</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min={1} max={6} value={form.passengers} onChange={set("passengers")}
            placeholder="Passengers" data-testid="edit-passengers"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
          <input type="number" min={0} max={480} step={5} value={form.wait_minutes} onChange={set("wait_minutes")}
            placeholder="Wait min at pickup" data-testid="edit-wait"
            className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
        </div>
        <textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Notes"
          data-testid="edit-notes"
          className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm resize-none" />

        {/* Pricing */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-emerald-900 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" /> Pricing overrides (dollars)
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs text-emerald-900">
              Quoted final fare
              <input type="number" step="0.01" value={form.suggested_final_fare_dollars} onChange={set("suggested_final_fare_dollars")}
                placeholder="0.00" data-testid="edit-suggested-fare"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm" />
            </label>
            <label className="text-xs text-emerald-900">
              Hold amount
              <input type="number" step="0.01" value={form.hold_amount_dollars} onChange={set("hold_amount_dollars")}
                placeholder="0.00" data-testid="edit-hold-amount"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm" />
            </label>
            <label className="text-xs text-emerald-900">
              Final captured
              <input type="number" step="0.01" value={form.final_fare_dollars} onChange={set("final_fare_dollars")}
                placeholder="0.00" data-testid="edit-final-fare"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm" />
            </label>
          </div>
          <div className="text-[10px] text-emerald-800 leading-relaxed">
            Editing pricing does NOT re-authorize the card. If a new hold is needed, use "Change hold" on the ride card.
          </div>
        </div>

        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
          rows={2} required data-testid="edit-reason"
          placeholder="Reason for these edits (required — audit log) *"
          className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-sm resize-none" />

        <div className="flex gap-2">
          <button type="button" onClick={onClose} data-testid="edit-cancel"
            className="flex-1 px-4 py-2.5 rounded-full border border-brand-navy/15 text-brand-navy hover:bg-brand-bg text-sm font-medium">
            Cancel
          </button>
          <button type="submit" disabled={busy || !reason.trim()} data-testid="edit-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark disabled:opacity-50 text-white font-semibold py-2.5 rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PencilLine className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
